import { useState } from 'react';
import type { VenueType } from '@vigilo/shared';
import {
  Btn, KLabel,
  FONT_SIZE, FONT_WEIGHT, RADIUS, SPACE,
  Icons,
  type ColorPalette,
} from '@vigilo/design-system';
import { useCrowdTaxonomy } from '../hooks/useCrowdTaxonomy';
import type { CreateVenueDto } from '../hooks/useVenues';

export function CreateVenueModal({ C, onClose, onCreate }: {
  C:        ColorPalette;
  onClose:  () => void;
  onCreate: (dto: CreateVenueDto) => Promise<void>;
}) {
  const { taxonomy, loading: taxonomyLoading } = useCrowdTaxonomy();
  const venueTypes = taxonomy?.venueTypes ?? [];
  const [name,     setName]     = useState('');
  const [vtype,    setVtype]    = useState<VenueType | ''>('');
  const [city,     setCity]     = useState('');
  const [country,  setCountry]  = useState('');
  const [address,  setAddress]  = useState('');
  const [capacity, setCapacity] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState<string | null>(null);

  const inputStyle = {
    background: C.surface, border: `1px solid ${C.border2}`, borderRadius: RADIUS.sm,
    padding: '8px 12px', fontSize: FONT_SIZE.body, color: C.text,
    fontFamily: "'Albert Sans', sans-serif", outline: 'none', width: '100%',
    boxSizing: 'border-box' as const,
  };

  async function handleSubmit() {
    if (!vtype) {
      setErr('Venue type is required.');
      return;
    }
    if (!name.trim() || !city.trim() || !country.trim() || !capacity) {
      setErr('Name, city, country and capacity are required.');
      return;
    }
    const cap = parseInt(capacity, 10);
    if (Number.isNaN(cap) || cap < 1) {
      setErr('Capacity must be a positive number.');
      return;
    }

    setSaving(true);
    setErr(null);
    try {
      await onCreate({
        name: name.trim(),
        venueType: vtype,
        city: city.trim(),
        country: country.trim().toUpperCase().slice(0, 2),
        address: address.trim() || undefined,
        totalCapacity: cap,
      });
      onClose();
    } catch {
      setErr('Failed to create venue. Check the Crowd API.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div
        data-testid="create-venue-modal"
        style={{
        background: C.surface, borderRadius: RADIUS.panel, border: `1px solid ${C.border2}`,
        width: 480, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto',
        fontFamily: "'Albert Sans', sans-serif",
      }}>
        <div style={{
          padding: `${SPACE[16]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
            Create Venue
          </span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
          >
            <Icons.X size={14} />
          </button>
        </div>

        <div style={{ padding: `${SPACE[16]}px ${SPACE[18]}px`, display: 'flex', flexDirection: 'column', gap: SPACE[12] }}>
          <div>
            <KLabel C={C}>Venue Name</KLabel>
            <input data-testid="venue-name-input" style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Venue name" autoFocus />
          </div>
          <div>
            <KLabel C={C}>Venue Type</KLabel>
            <select
              data-testid="venue-type-select"
              style={inputStyle}
              value={vtype}
              onChange={e => setVtype(e.target.value as VenueType)}
              disabled={taxonomyLoading || venueTypes.length === 0}
            >
              <option value="">{taxonomyLoading ? 'Loading venue types...' : 'Select venue type'}</option>
              {venueTypes.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[10] }}>
            <div>
              <KLabel C={C}>City</KLabel>
              <input data-testid="venue-city-input" style={inputStyle} value={city} onChange={e => setCity(e.target.value)} placeholder="City" />
            </div>
            <div>
              <KLabel C={C}>Country (ISO 2)</KLabel>
              <input data-testid="venue-country-input" style={inputStyle} value={country} onChange={e => setCountry(e.target.value)} placeholder="IN" maxLength={2} />
            </div>
          </div>
          <div>
            <KLabel C={C}>Address</KLabel>
            <input data-testid="venue-address-input" style={inputStyle} value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, postcode (optional)" />
          </div>
          <div>
            <KLabel C={C}>Total Capacity</KLabel>
            <input data-testid="venue-capacity-input" style={inputStyle} type="number" min={1} value={capacity} onChange={e => setCapacity(e.target.value)} placeholder="Capacity" />
          </div>

          {err && (
            <div style={{
              background: C.emberDim, border: `1px solid ${C.ember}`, borderRadius: RADIUS.sm,
              padding: `${SPACE[10]}px ${SPACE[12]}px`, fontSize: FONT_SIZE.base, color: C.ember,
            }}>{err}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACE[8], paddingTop: SPACE[4] }}>
            <Btn C={C} variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn C={C} variant="primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Creating...' : 'Create Venue'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
